import { useContext, useEffect, useMemo, useState } from "react";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from "@mui/material";
import { allChangeOrderOptions } from "../../apiCalls";
import { dispatchError } from "../../common/fx";
import { UIContext } from "../../providers/UIProvider";

type Option = {
  id: string;
  optionCode?: string;
  description?: string;
  salesPrice?: number;
};

type Props = {
  isOpen: boolean;
  jobId: number;
  loading: boolean;
  onConfirm: () => void;
  result: "success" | "failure" | null;
  selectedOptions: Option[];
  setIsOpen: (value: boolean) => void;
};

const formatCurrency = (value: number | undefined) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(value ?? 0));

const SubmitOptionsConfirmation = ({
  isOpen,
  jobId,
  loading,
  onConfirm,
  result,
  selectedOptions,
  setIsOpen,
}: Props) => {
  const [, dispatch] = useContext<any>(UIContext);
  const [submittedStructuralOptions, setSubmittedStructuralOptions] = useState<
    Option[]
  >([]);
  const [checkingStructuralOptions, setCheckingStructuralOptions] =
    useState(false);
  const [structuralOptionsCheckFailed, setStructuralOptionsCheckFailed] =
    useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setCheckingStructuralOptions(true);
    setStructuralOptionsCheckFailed(false);

    allChangeOrderOptions(
      {
        JobId: jobId,
        highlandCategoryType: "Structural",
        statuses: ["Submitted"],
        pageSize: 10000,
        pageNumber: 1,
        includeCancelationOption: false,
      },
      (res: any) => {
        setSubmittedStructuralOptions(res.data.value ?? []);
        setCheckingStructuralOptions(false);
      },
      (err: any) => {
        setSubmittedStructuralOptions([]);
        setCheckingStructuralOptions(false);
        setStructuralOptionsCheckFailed(true);
        dispatch(dispatchError(err.response?.data ?? err));
      }
    );
  }, [dispatch, isOpen, jobId]);

  const omittedStructuralOptionCount = useMemo(() => {
    const selectedOptionIds = new Set(
      selectedOptions.map((option) => option.id.toLowerCase())
    );

    return submittedStructuralOptions.filter(
      (option) => !selectedOptionIds.has(option.id.toLowerCase())
    ).length;
  }, [selectedOptions, submittedStructuralOptions]);

  return (
    <Dialog
      data-testid="submit-options-confirmation"
      fullWidth
      maxWidth="md"
      onClose={() => !loading && setIsOpen(false)}
      open={isOpen}
    >
      <DialogTitle
        sx={(theme: Theme) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
        })}
      >
        {result === "failure"
          ? "Send to Studio H failed"
          : "Send to Studio H (Envision)"}
      </DialogTitle>
      <DialogContent>
        {result === "success" ? (
          <Typography sx={{ marginY: 2 }}>
            {selectedOptions.length}{" "}
            {selectedOptions.length === 1 ? "option is" : "options are"} being
            sent to Studio H/Envision and will be shown as Confirmed.
          </Typography>
        ) : result === "failure" ? (
          <Typography sx={{ marginY: 2 }}>
            Send to Studio H failed. No options were sent.
          </Typography>
        ) : (
          <>
            <Typography sx={{ marginY: 2 }}>
              They'll land in Studio H (Envision) as{" "}
              <strong>Confirmed</strong>. The Change Order does not change apart
              from the Studio H status on each row.
            </Typography>

            <TableContainer sx={{ maxHeight: 360 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedOptions.map((option) => (
                    <TableRow key={option.id}>
                      <TableCell>{option.optionCode}</TableCell>
                      <TableCell>{option.description}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(option.salesPrice)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {checkingStructuralOptions && (
              <LinearProgress sx={{ marginTop: 2 }} />
            )}

            {!checkingStructuralOptions && structuralOptionsCheckFailed && (
              <Alert severity="error" sx={{ marginTop: 2 }}>
                Unable to verify whether any submitted structural options were
                omitted.
              </Alert>
            )}

            {!checkingStructuralOptions &&
              !structuralOptionsCheckFailed &&
              omittedStructuralOptionCount > 0 && (
                <Alert severity="warning" sx={{ marginTop: 2 }}>
                  <Typography>
                    <strong>
                      {omittedStructuralOptionCount} submitted structural{" "}
                      {omittedStructuralOptionCount === 1
                        ? "option is"
                        : "options are"}{" "}
                      not selected
                    </strong>
                  </Typography>
                  <Typography variant="body2">
                    Studio H (Envision) stores a full snapshot of the home, so
                    options left out of this send may be removed there.
                  </Typography>
                </Alert>
              )}

            {!checkingStructuralOptions &&
              !structuralOptionsCheckFailed &&
              omittedStructuralOptionCount === 0 && (
                <Alert severity="success" sx={{ marginTop: 2 }}>
                  <strong>
                    All ({submittedStructuralOptions.length}) submitted
                    structural options are selected to be sent to Studio H (Envision).
                  </strong>
                </Alert>
              )}
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{ justifyContent: result ? "flex-end" : "space-between" }}
      >
        {result ? (
          <Button
            color="primary"
            onClick={() => setIsOpen(false)}
            variant="outlined"
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              color="primary"
              disabled={loading}
              onClick={() => setIsOpen(false)}
              variant="outlined"
            >
              Cancel
            </Button>
            <LoadingButton
              color="primary"
              disabled={
                checkingStructuralOptions || structuralOptionsCheckFailed
              }
              loading={loading}
              onClick={onConfirm}
              variant="contained"
            >
              Send to Studio H
            </LoadingButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SubmitOptionsConfirmation;
